import { type ReactNode } from "react"
import clsx from "clsx"
import { useFormContext } from "react-hook-form"

import { Accordion, AccordionPanel } from "@unlikelystudio/react-abstract-components"

import { BoxCheckbox } from "~/components/ui/Collection/CollectionDefault/components/PanelFilters/FilterFacet/Box"
import { StandardCheckbox } from "~/components/ui/Collection/CollectionDefault/components/PanelFilters/FilterFacet/Standard"
import { SwatchCheckbox } from "~/components/ui/Collection/CollectionDefault/components/PanelFilters/FilterFacet/Swatch"
import Icon from "~/components/abstracts/Icon"
import {
  useSearchAndDiscoveryContext,
  type SearchAndDiscoveryContextState,
} from "~/providers/SearchAndDiscoveryProvider"
import { useFiltersAccordionState } from "~/providers/SearchAndDiscoveryProvider/FiltersAccordionStateProvider"
import { fromBezierToArray } from "~/utils/from-bezier-to-array"
import { objectEntries } from "~/utils/object-entries"

import { easings } from "~/styles/variables/easings"

import * as css from "./styles.css"

export type CheckboxType = "Box" | "Swatch" | "List"

const CHECKBOX_TYPE_BY_NAME: Record<string, CheckboxType> = {
  filters_color_category: "Swatch",
}

export function FilterFacet({ name, facets = [] }: { name: string; facets: SearchAndDiscoveryContextState["facets"] }) {
  const { colorsWithThumbnail, filters } = useSearchAndDiscoveryContext()
  const [openAccordions, setOpenAccordions] = useFiltersAccordionState()

  const isOpen = openAccordions?.includes?.(name)

  const facet = facets?.find((facet) => facet.processedName === name)
  const { label, values } = facet ?? {}

  const { register } = useFormContext() ?? {}

  const type = CHECKBOX_TYPE_BY_NAME?.[name as string] ?? "Box"

  const multipleColumns = Boolean((type === "List" || type === "Swatch") && facets?.length > 1)

  if (facets?.length <= 1 || (values && values?.length <= 1)) {
    return null
  }

  const hasOtherFiltersSelected = objectEntries(filters)
    ?.filter(([key]) => key !== name)
    ?.some(([_, values]) => values?.length > 0)

  function renderCheckbox(entry: NonNullable<typeof values>[number]) {
    const facetValue = entry?.label

    if (!facetValue) return null

    const isEnabled = Boolean(entry?.count) || (!Boolean(entry?.count) && !hasOtherFiltersSelected)

    const components: Record<CheckboxType, ReactNode> = {
      Box: (
        <BoxCheckbox value={facetValue} disabled={!isEnabled} label={facetValue} id={facetValue} {...register(name)} />
      ),
      List: (
        <StandardCheckbox
          type="checkbox"
          value={facetValue}
          disabled={!isEnabled}
          id={facetValue}
          {...register(name)}
        />
      ),
      Swatch: (
        <SwatchCheckbox
          value={facetValue}
          disabled={!isEnabled}
          label={facetValue}
          id={facetValue}
          colorHexadecimal={colorsWithThumbnail?.find((color) => color?.label === facetValue)?.colorValue}
          {...register(name)}
        />
      ),
    }

    return components?.[type] ?? null
  }

  return (
    <div className={css.accordionWrapper}>
      <Accordion {...(isOpen && { index: 0 })} autoHide={false}>
        <AccordionPanel
          index={0}
          onClose={() => {
            if (openAccordions?.includes?.(name)) {
              setOpenAccordions((state) => state?.filter?.((accordion) => accordion !== name))
            }
          }}
          onOpen={() => {
            if (!openAccordions?.includes?.(name)) {
              setOpenAccordions((state) => [...state, name])
            }
          }}
          headClassName={clsx(css.panelHead)}
          transition={{ duration: 0.5, ease: fromBezierToArray(easings["easeInOutAccordion"]) }}
          ariaLabels={{ close: "close", open: "open" }}
          head={(isActive) => (
            <>
              {label}
              <Icon name={isActive ? "ChevronUp" : "ChevronDown"} width={14} theme="blue-deep" />
            </>
          )}
          body={
            <div className={clsx(css.panelBody?.[type], { [css.multipleColumns]: multipleColumns })}>
              {values
                ?.filter((value) => Boolean(value?.label))
                ?.map((entry, index) => {
                  return (
                    <div key={`facetValue-${name}-${index}`} className={css.option}>
                      {renderCheckbox(entry)}
                    </div>
                  )
                })}
            </div>
          }
        />
      </Accordion>
    </div>
  )
}
